import React, { useMemo, useState } from "react";
import * as Yup from "yup";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import Input from "../shared/TailwindComponents/Input";
import Dropdown, { DropdownOption } from "../shared/TailwindComponents/Dropdown";
import { FolderProps } from "./documents/types";
import Button from "../shared/TailwindComponents/Button";
import { useHistory } from "react-router-dom";
import { useAppDispatch, RootState } from "../../store";
import { createFolderAction } from "../../store/actions/FolderAction";
import { ShareUser } from "../shared/ShareAccessManager";
import CollaboratorSelector, { Collaborator } from "../shared/CollaboratorSelector";

type Props = {
  onClose: () => void;
  existingFolders: FolderProps[];
  selectedFolderItem?: any;
  handleCreateFolder: (data: {
    folderId?: string;
    folderName?: string;
  }) => void | Promise<void>;
  employees: ShareUser[];
  initialShared: ShareUser[];
  onRefresh?: (createdFolderId?: string) => void | Promise<void>;
};

const CreateFolderOnly: React.FC<Props> = ({
  onClose,
  existingFolders,
  selectedFolderItem,
  employees,
  initialShared,
  onRefresh,
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const userInfo = useSelector((state: RootState) => state.user.userInfo);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [collaborators, setCollaborators] = useState<Collaborator[]>([]);
  const [selectedFolderId, setSelectedFolderId] = useState<string>("");


  const folderOptions = useMemo(() => {
    const options =
      (existingFolders || [])
        .filter((f) => f.FolderType !== "Locked")
        .map((f) => ({
          label:
            (f as any).FolderName ??
            (f as any).name ??
            (f as any).title ??
            String(f.id ?? f.FolderID ?? ""),
          value: String(f.id ?? f.FolderID ?? ""),
        })) || [];

    if (selectedFolderItem && options.length >= 0) {
      options.push({
        label: selectedFolderItem.FolderName,
        value: selectedFolderItem.FolderID,
      });
    }

    return options;
  }, [existingFolders, selectedFolderItem]);

  const validationSchema = useMemo(() => {
    return Yup.object().shape({
      FolderId: Yup.string().optional(),
      FolderName: Yup.string()
        .trim()
        .required(t("Folder name is required")),
    });
  }, [t]);

  const formik = useFormik({
    initialValues: {
      FolderId: (selectedFolderItem && selectedFolderItem?.FolderID) || "",
      FolderName: "",
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      try {
        setIsSubmitting(true);
        setError(null);

        // Prepare collaborators with access levels
        const validCollaborators = collaborators
          .map(c => ({
            user_id: parseInt(String(c.user_id), 10),
            access: c.access
          }))
          .filter(c => !Number.isNaN(c.user_id));

        const response = await dispatch(
          createFolderAction({
            FolderName: values.FolderName,
            ParentFolderID: values.FolderId || undefined,
            collaborators: validCollaborators.length > 0 ? validCollaborators : undefined,
          })
        );

        const createdFolderId = response?.FolderID || response?.id;

        if (onRefresh && createdFolderId) {
          await onRefresh(createdFolderId);
        }

        onClose();
      } catch (e: any) {
        setError(e?.message || t("Error creating folder"));
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  return (
    <div className="flex flex-col px-6 py-6 w-[700px] mt-[-20px] gap-6">
      <form onSubmit={formik.handleSubmit}>
        <div className="grid grid-cols-2 gap-4 mb-2">
          <div>
            <Input
              label={t("Folder Name")}
              type="text"
              name="FolderName"
              value={formik.values.FolderName}
              onChange={formik.handleChange}
              error={
                formik.touched.FolderName
                  ? (formik.errors as any).FolderName
                  : undefined
              }
              placeholder={t("e.g., 2025 Cases")}
            />
          </div>

          <div>
            <Dropdown
              label={t("Parent Folder")}
              description={t("Leave empty to create the folder at the root level.")}
              infoText={t("You can select a parent folder or create at root level.")}
              options={folderOptions}
              value={formik.values.FolderId}
              onChange={(val) => {
                formik.setFieldValue("FolderId", val);
                setSelectedFolderId((Array.isArray(val) ? val[0] : val) || "");
              }}
              error={
                formik.touched.FolderId ? (formik.errors as any).FolderId : undefined
              }
            />
          </div>
        </div>

        {error && (
          <div className="mt-3 p-3 bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800 rounded text-danger-600 dark:text-danger-400 text-sm">
            {error}
          </div>
        )}

        <CollaboratorSelector
          collaborators={collaborators}
          onCollaboratorsChange={setCollaborators}
          initialShared={selectedFolderId ? initialShared : []}
        />

        <div className="flex justify-end px-4 mt-6">
          <Button
            variant="primary"
            size="small"
            type="submit"
            disabled={isSubmitting}
          >
            {isSubmitting ? t("Creating...") : t("Create Folder")}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default CreateFolderOnly;
